import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { fmtR } from "./useScorecard";
import { EntryModal } from "./ScorecardModals";
import { GroupTag } from "./ScorecardBoard";
import { Pencil, Download } from "lucide-react";

const GROUPS = [["none", "No grouping"], ["month", "By month"], ["year", "By year"], ["project", "By project"], ["user", "By employee"]];

// Full history sheet — management page (all staff, editors) and staff profile tab (one person).
export default function ScorecardHistory({ userId, projectId, showUser = true }) {
  const [f, setF] = useState({ project_id: projectId || "", kind: "", date_from: "", date_to: "", q: "", sort: "-entry_date", include_voided: false });
  const [group, setGroup] = useState("none");
  const [data, setData] = useState({ items: [], totals: {}, can_edit: false });
  const [edit, setEdit] = useState(null);
  const [projects, setProjects] = useState([]);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries({ ...f, user_id: userId }).filter(([, v]) => v !== "" && v !== false && v != null));
    api.get("/scorecard/entries", { params }).then(({ data: d }) => setData(d)).catch(() => {});
  }, [f, userId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/scorecard", { params: { include_closed: true } }).then(({ data: d }) =>
      setProjects((d.projects || []).map((p) => ({ id: p.project_id, name: p.project_name })))).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    if (group === "none") return [["", data.items]];
    const key = (e) => (group === "month" ? (e.entry_date || "").slice(0, 7) : group === "year" ? (e.entry_date || "").slice(0, 4)
      : group === "project" ? e.project_name || "—" : e.user_name || "—");
    const m = new Map();
    data.items.forEach((e) => { const k = key(e); m.set(k, [...(m.get(k) || []), e]); });
    return [...m.entries()];
  }, [data.items, group]);

  const sub = (rows) => {
    const live = rows.filter((r) => !r.voided_at);
    const p = live.filter((r) => r.kind === "penalty").reduce((a, r) => a + r.amount, 0);
    const w = live.filter((r) => r.kind === "reward").reduce((a, r) => a + r.amount, 0);
    return { p, w, net: w - p };
  };
  const exportCsv = () => {
    const head = ["Date", "Employee", "Project", "Type", "Amount", "Reason", "Logged by", "Voided"];
    const lines = data.items.map((e) => [e.entry_date, e.user_name, e.project_name, e.kind, e.amount, e.reason, e.created_by_name, e.voided_at ? "yes" : ""]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const url = URL.createObjectURL(new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" }));
    Object.assign(document.createElement("a"), { href: url, download: "scorecard-history.csv" }).click();
  };
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className="space-y-3" data-testid="scorecard-history">
      <div className="flex flex-wrap gap-2 items-end text-xs">
        {!projectId && (
          <select className="hv-input !w-auto" value={f.project_id} onChange={set("project_id")} data-testid="scorecard-history-filter-project">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <select className="hv-input !w-auto" value={f.kind} onChange={set("kind")} data-testid="scorecard-history-filter-kind">
          <option value="">Penalties + rewards</option><option value="penalty">Penalties</option><option value="reward">Rewards</option>
        </select>
        <input type="date" className="hv-input !w-auto" value={f.date_from} onChange={set("date_from")} data-testid="scorecard-history-filter-from" />
        <input type="date" className="hv-input !w-auto" value={f.date_to} onChange={set("date_to")} data-testid="scorecard-history-filter-to" />
        <input className="hv-input !w-40" placeholder="Search reason…" value={f.q} onChange={set("q")} data-testid="scorecard-history-filter-search" />
        <select className="hv-input !w-auto" value={f.sort} onChange={set("sort")} data-testid="scorecard-history-sort">
          <option value="-entry_date">Newest first</option><option value="entry_date">Oldest first</option>
          <option value="-amount">Largest amount</option><option value="amount">Smallest amount</option>
        </select>
        <select className="hv-input !w-auto" value={group} onChange={(e) => setGroup(e.target.value)} data-testid="scorecard-history-group">
          {GROUPS.filter(([k]) => showUser || k !== "user").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="flex items-center gap-1 text-white/60"><input type="checkbox" checked={f.include_voided} onChange={set("include_voided")} /> Show voided</label>
        <button className="hv-btn-ghost inline-flex items-center gap-1 px-2 py-1" onClick={exportCsv} data-testid="scorecard-history-export"><Download className="w-3 h-3" /> CSV</button>
      </div>
      <div className="flex gap-4 text-xs" data-testid="scorecard-history-totals">
        <span className="text-rose-300">Penalties −{fmtR(data.totals.penalties)}</span>
        <span className="text-emerald-300">Rewards +{fmtR(data.totals.rewards)}</span>
        <span className={(data.totals.net || 0) < 0 ? "text-rose-400 font-semibold" : "text-cyan-200 font-semibold"}>Net {fmtR(data.totals.net)}</span>
        <span className="text-white/40">{data.totals.count || 0} entries</span>
      </div>
      {groups.map(([g, rows]) => (
        <div key={g || "all"} className="rounded-lg border border-white/10 overflow-x-auto">
          {g && (() => { const s = sub(rows); return (
            <div className="px-3 py-1.5 bg-white/[0.03] text-xs flex gap-3"><span className="font-semibold text-white">{g}</span>
              <span className="text-rose-300">−{fmtR(s.p)}</span><span className="text-emerald-300">+{fmtR(s.w)}</span><span className="text-white/70">net {fmtR(s.net)}</span></div>); })()}
          <table className="w-full min-w-[640px] text-xs">
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className={`border-t border-white/5 ${e.voided_at ? "opacity-40 line-through" : ""}`} data-testid={`scorecard-history-row-${e.id}`}>
                  <td className="py-1.5 pl-3 font-mono text-white/50 w-24">{e.entry_date}</td>
                  {showUser && <td className="py-1.5 text-white">{e.user_name}</td>}
                  <td className="py-1.5 text-white/60">{e.project_name}</td>
                  <td className={`py-1.5 uppercase text-[10px] font-semibold ${e.kind === "penalty" ? "text-rose-300" : "text-emerald-300"}`}>{e.kind}</td>
                  <td className={`py-1.5 font-mono text-right ${e.kind === "penalty" ? "text-rose-200" : "text-emerald-200"}`}>{e.kind === "penalty" ? "−" : "+"}{fmtR(e.amount)}</td>
                  <td className="py-1.5 pl-3 text-white/80">{e.reason}<GroupTag e={e} /></td>
                  <td className="py-1.5 text-white/40">{e.created_by_name}</td>
                  <td className="py-1.5 pr-2 w-6">{data.can_edit && <button onClick={() => setEdit(e)} className="text-white/50 hover:text-white" data-testid={`scorecard-history-edit-${e.id}`}><Pencil className="w-3 h-3" /></button>}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td className="p-3 text-white/40">Nothing matches these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      ))}
      {edit && <EntryModal entry={edit} member={{ first_name: edit.user_name }} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}
